/**
 * Salesforce Authentication Hooks using GraphQL + React Query
 * 
 * Replaces REST API calls with GraphQL queries for Salesforce OAuth flow
 * 
 * @module hooks/auth/useSalesforceAuthGraphQL
 */

import { useMutation } from '@tanstack/react-query';
import { getAuthorizationUrl } from '@/services/salesforce/auth.graphql';
import type { SalesforceEnvironment } from '@/services/salesforce';
import toast from 'react-hot-toast';


/**
 * Hook to fetch Salesforce authorization URL using GraphQL + React Query
 * 
 * This hook uses useMutation to trigger the GraphQL query on demand.
 * Returns the authorization URL and state for OAuth flow.
 * 
 * @example
 * const getAuthUrl = useGetAuthorizationUrl();
 * 
 * getAuthUrl.mutate('sandbox', {
 *   onSuccess: (data) => {
 *     // Redirect to Salesforce login
 *     window.location.href = data.authorizationUrl.url;
 *   }
 * });
 */
export function useGetAuthorizationUrl() {
  return useMutation({
    mutationFn: (environment: SalesforceEnvironment) =>
      getAuthorizationUrl(environment),
    onError: (error: any) => {
      console.error('❌ Failed to get authorization URL:', error);
      const errorMessage = 
        error.message || 
        'Failed to initiate authentication';
      toast.error(errorMessage);
    },
  });
}

